import {
  FC,
  FocusEvent,
  useState,
  ChangeEvent,
  KeyboardEvent,
  useRef
} from "react"
import { X } from "lucide-react"
import { useIntegrationSettingsForm } from "../../../../hooks"
import { useIsSuperAdmin } from "../../../../hooks/use-user-roles"
import Form from "../../../form"
import { CustomParam, Integration } from "../../../../../types"

interface Props {
  integration?: Integration
  open: boolean
}

interface ToggleTileProps {
  label: string
  desc: string
  checked: boolean
  onChange: () => void
}

const ToggleTile: FC<ToggleTileProps> = ({
  label,
  desc,
  checked,
  onChange
}) => (
  <div className="flex items-center justify-between px-3.5 py-3 bg-neutral-50 rounded border border-neutral-100">
    <div className="min-w-0 mr-3">
      <div className="text-[13px] font-medium text-neutral-800">{label}</div>
      <div className="text-[11px] text-neutral-400 mt-0.5">{desc}</div>
    </div>
    <button
      type="button"
      onClick={onChange}
      className={[
        "relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 cursor-pointer focus:outline-none",
        checked ? "bg-primary-500" : "bg-neutral-200"
      ].join(" ")}
      role="switch"
      aria-checked={checked}
    >
      <span
        aria-hidden="true"
        className={[
          "pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition duration-200",
          checked ? "translate-x-4" : "translate-x-0"
        ].join(" ")}
      />
    </button>
  </div>
)

const CUSTOM_PARAMS = [
  { key: "utm_*", scope: "lead" },
  { key: "gclid", scope: "lead" }
]

const AdvanceOptions: FC<Props> = ({ integration, open }) => {
  const isSuperAdmin = useIsSuperAdmin()
  const {
    saveSetting,
    handleFormikBlur,
    handleFormikChange,
    saveSettingLocal,
    values
  } = useIntegrationSettingsForm(integration)

  const [newParam, setNewParam] = useState("")
  const tagInputRef = useRef<HTMLInputElement>(null)

  const settings = values.app_settings?.settings
  const customParams: CustomParam[] = settings?.customParams ?? []
  const hasUTM = customParams.length > 0

  const onChange = (
    name: string,
    value: string | number | boolean | null | any[]
  ) => {
    saveSetting({ [name]: value })
  }

  const isApiDisabled = !(settings?.expose_api ?? true)

  const addParam = () => {
    const key = newParam.trim()
    if (!key || customParams.some(p => p.key === key)) return
    onChange("customParams", [...customParams, { key, scope: "lead" }])
    setNewParam("")
  }

  const onTagInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      addParam()
    } else if (e.key === "Backspace" && !newParam && customParams.length > 0) {
      removeParam(customParams[customParams.length - 1].key)
    }
  }

  const removeParam = (key: string) => {
    onChange(
      "customParams",
      customParams.filter(p => p.key !== key)
    )
  }

  const onBlurPOSTURL = (e: FocusEvent<HTMLInputElement>) => {
    onChange("postUrl", e.target.value)
    handleFormikBlur(e)
  }

  const onChangePOSTURL = (e: ChangeEvent<HTMLInputElement>) => {
    saveSettingLocal({ postUrl: e.target.value })
    handleFormikChange(e)
  }

  if (!open) return null

  return (
    <div className="mt-5 pt-5 border-t border-neutral-100">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Left column: toggle tiles */}
        <div className="flex flex-col gap-2.5">
          <ToggleTile
            label="Disable API Access"
            desc="Restrict programmatic data retrieval"
            checked={isApiDisabled}
            onChange={() => onChange("expose_api", isApiDisabled)}
          />
          <ToggleTile
            label="Disable Calculator Cards"
            desc="Compact view for mobile embeds"
            checked={settings?.disableCalcCards ?? false}
            onChange={() =>
              onChange(
                "disableCalcCards",
                !(settings?.disableCalcCards ?? false)
              )
            }
          />
          <ToggleTile
            label="Enable Global Exit Popup"
            desc="Show exit-intent popup before users leave the page"
            checked={settings?.globalExitPopUpEnable ?? false}
            onChange={() =>
              onChange(
                "globalExitPopUpEnable",
                !(settings?.globalExitPopUpEnable ?? false)
              )
            }
          />
          {isSuperAdmin && (
            <ToggleTile
              label="Billing Exempt"
              desc="Exclude from billing calculations"
              checked={settings?.billing_exempt ?? false}
              onChange={() =>
                onChange("billing_exempt", !(settings?.billing_exempt ?? false))
              }
            />
          )}
        </div>

        {/* Right column: POST URL + UTM */}
        <div className="flex flex-col gap-4">
          <Form.Input
            inputProps={{
              onBlur: onBlurPOSTURL,
              onChange: onChangePOSTURL,
              value: settings?.postUrl ?? ""
            }}
            label="POST Webhook URL"
            name="postUrl"
            placeholder="https://api.yourservice.com/webhook"
          />
          <div>
            <div className="text-[11px] font-medium uppercase tracking-wider text-neutral-400 mb-1.5">
              UTM Tracking Params
            </div>
            {!hasUTM ? (
              <button
                type="button"
                onClick={() => onChange("customParams", CUSTOM_PARAMS)}
                className="inline-flex items-center gap-1.5 h-8 px-3.5 border border-[#63C7C4] rounded text-sm font-medium text-[#2A8C89] bg-white hover:bg-teal-50 transition-colors cursor-pointer"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add UTM Tracking
              </button>
            ) : (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-neutral-500">
                    Custom params
                  </span>
                  <button
                    type="button"
                    onClick={() => onChange("customParams", [])}
                    className="text-xs text-red-400 hover:text-red-500"
                  >
                    Remove all
                  </button>
                </div>
                <div
                  className="flex flex-wrap gap-1.5 rounded border border-neutral-300 px-2 py-2 cursor-text focus-within:ring-1 focus-within:ring-primary-300 focus-within:border-primary-300"
                  onClick={() => tagInputRef.current?.focus()}
                >
                  {customParams.map(param => (
                    <span
                      key={param.key}
                      className="inline-flex items-center gap-1 h-7 px-2.5 rounded bg-teal-50 border border-teal-200/60 text-xs font-mono font-medium text-[#2A8C89]"
                    >
                      {param.key}
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation()
                          removeParam(param.key)
                        }}
                        className="text-teal-400 hover:text-teal-600"
                        aria-label={`Remove ${param.key}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    ref={tagInputRef}
                    type="text"
                    value={newParam}
                    onChange={e => setNewParam(e.target.value)}
                    onKeyDown={onTagInputKeyDown}
                    placeholder={
                      customParams.length ? "" : "utm_source, gclid…"
                    }
                    className="min-w-[8rem] flex-1 bg-transparent text-sm outline-none placeholder:text-neutral-400 border-none focus:ring-0 focus:border-none"
                  />
                </div>
                <p className="text-xs text-neutral-400">
                  Enter to add · Backspace to remove last
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdvanceOptions
